import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Button, Grid } from '@mui/material';

import { OptionalTooltip } from '@paragon/material';

import { getAppMonitor } from '../../../../monitor';
import { RootState } from '../../../../store';
import {
  setDisplayAnalogModel,
  toggleCheckResultsLoadCasesVisibility,
  toggleShowAnalogPanelsAndShowAnalogModel,
  toggleSingleDisplayedForces,
} from '../../../../store/actions/visualOptionActions';
import { getActiveAnalysisSet } from '../../../../store/selectors/getActiveAnalysisSetSelector';
import { getAnalysisResultGuids } from '../../../../store/selectors/getAnalysisResultsForActiveAnalysisSetSelector';
import {
  getLoadCasesForActiveComponentDesignSorted,
  LoadCaseWithCapacity,
} from '../../../../store/selectors/getLoadCasesForActiveComponentDesignSortedSelector';
import DisplayForceOptionButton from './DisplayForceOptionButton';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

class CheckResultsShortcuts extends React.Component<Props> {
  handleDisplayedForcesUpdate = (selectedForceDisplay: string) => {
    this.props.toggleSingleDisplayedForces(selectedForceDisplay);
    getAppMonitor().recordInteraction('Displayed forces manually toggled', { selectedForceDisplay });
  };

  toggleAnalogModel = () => {
    this.props.setDisplayAnalogModel(!this.props.showAnalogModel);

    getAppMonitor().recordInteraction('Analog model visibility manually toggled', {
      previousState: {
        showAnalogModel: this.props.showAnalogModel,
        showAnalogPanels: this.props.showAnalogPanels,
      },
    });
  };

  toggleAnalogPanels = () => {
    this.props.toggleShowAnalogPanels();

    getAppMonitor().recordInteraction('Analog panels visibility manually toggled', {
      previousState: {
        showAnalogModel: this.props.showAnalogModel,
        showAnalogPanels: this.props.showAnalogPanels,
      },
    });
  };

  toggleLoadCases = () => {
    const selectedLoadCase: LoadCaseWithCapacity | undefined = this.props.loadCases.find(
      (loadCase) => loadCase.guid === this.props.selectedLoadCaseGuid,
    );
    this.props.toggleCheckResultsLoadCasesVisibility(selectedLoadCase!);

    getAppMonitor().recordInteraction('Load cases visibility manually toggled', { selectedLoadCase });
  };

  render() {
    return (
      <Grid container spacing={1} sx={{ padding: 2 }}>
        <Grid size={6}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={this.toggleAnalogModel}
            startIcon={this.props.showAnalogModel ? <VisibilityIcon /> : <VisibilityOffIcon />}
          >
            Analog Model
          </Button>
        </Grid>

        <Grid size={6}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={this.toggleAnalogPanels}
            startIcon={this.props.showAnalogPanels ? <VisibilityIcon /> : <VisibilityOffIcon />}
          >
            Analog Panels
          </Button>
        </Grid>

        <Grid size={6}>
          <OptionalTooltip
            title={
              this.props.isAnalysisResultUnsaved
                ? 'Detailed analysis required'
                : !this.props.selectedLoadCaseGuid
                  ? 'Select a load case'
                  : null
            }
            visible={this.props.isAnalysisResultUnsaved || !this.props.selectedLoadCaseGuid}
          >
            <DisplayForceOptionButton
              handleDisplayedForcesUpdate={this.handleDisplayedForcesUpdate}
              displayedForces={this.props.displayedForces}
              disabled={!this.props.selectedLoadCaseGuid || this.props.isAnalysisResultUnsaved}
            />
          </OptionalTooltip>
        </Grid>

        <Grid size={6}>
          <OptionalTooltip visible={!this.props.selectedLoadCaseGuid} title="Select a load case">
            <Button
              onClick={this.toggleLoadCases}
              size="small"
              fullWidth
              variant="outlined"
              disabled={!this.props.selectedLoadCaseGuid}
              startIcon={this.props.showCheckResultsLoadCases ? <VisibilityIcon /> : <VisibilityOffIcon />}
            >
              Loads
            </Button>
          </OptionalTooltip>
        </Grid>
      </Grid>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const analysisSet = getActiveAnalysisSet(state);
  return {
    showAnalogModel: state.visualOptions.showAnalogModel,
    showAnalogPanels: state.visualOptions.showAnalogPanels,
    displayedForces: state.visualOptions.displayedForces,
    loadCases: getLoadCasesForActiveComponentDesignSorted(state),
    selectedLoadCaseGuid: state.selections.selectedCheckResultsLoadCaseGuid,
    showCheckResultsLoadCases: state.visualOptions.showCheckResultsLoadCases,
    isAnalysisResultUnsaved: (analysisSet && !getAnalysisResultGuids(analysisSet)) || false,
  };
};

const actionCreators = {
  toggleShowAnalogPanels: toggleShowAnalogPanelsAndShowAnalogModel,
  setDisplayAnalogModel,
  toggleSingleDisplayedForces,
  toggleCheckResultsLoadCasesVisibility,
};

export default connect(mapStateToProps, actionCreators)(CheckResultsShortcuts);
